import { DiscardDialog } from '@/components/admin/versioned/discard-dialog';
import { EditorHeader } from '@/components/admin/versioned/editor-header';
import type { EditorHeaderFields, EditorHeading } from '@/components/admin/versioned/editor-header';
import { HistoryDrawer } from '@/components/admin/versioned/history-drawer';
import { PassageBox } from '@/components/admin/versioned/passage-box';
import { RetireDialog } from '@/components/admin/versioned/retire-dialog';
import { RetirementPanel } from '@/components/admin/versioned/retirement-panel';
import { SaveDialog } from '@/components/admin/versioned/save-dialog';
import { UsagePanel } from '@/components/admin/versioned/usage-panel';
import { VersionHistory } from '@/components/admin/versioned/version-history';
import { VersionedTabs } from '@/components/admin/versioned/versioned-tabs';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { useAdminSession } from '@/lib/admin-session';
import type { VersionedApi } from '@/lib/api';
import { isEditableTarget } from '@/lib/editable-target';
import { CASCADE_QUERY_PREFIXES, useProdLinked } from '@/lib/promotion-api';
import type { UndoStack } from '@/lib/undo-stack';
import {
  canRedo,
  canUndo,
  openUndoStack,
  recordUndo,
  redoEntry,
  undoEntry,
} from '@/lib/undo-stack';
import { useIsVitrine } from '@/lib/use-instance';
import type {
  BrowserDraftStatus,
  VersionedBrowserDraft,
  VersionedDraftKit,
} from '@/lib/versioned-draft';
import { canSaveVersion, nextVersionNumber, structurallyEqual } from '@/lib/versioned-draft';
import type { VersionedWording } from '@/lib/versioned-labels';
import { isDraftStale, versionedRefusal, versionedRefusalOf } from '@/lib/versioned-labels';
import { VITRINE_WORDING } from '@/lib/vitrine';
import type {
  ReferentRef,
  SaveDraftRequest,
  SaveVersionRequest,
  UsageLine,
  VersionSummary,
  VersionTag,
  VersionedDetail,
  VersionedVersion,
} from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';

/**
 * L'éditeur d'un objet versionné - two columns, its button row, three boxes
 * (refonte modulaire §4; maquettes `Editeur` / `NouveauCritere`).
 *
 * Everything on this screen except the FORM is the pattern of §4: the head with
 * its two pastilles and its button row, the history and its five gestures,
 * « utilisé dans », the retirement, the three boxes, the debounced autosave and
 * its optimistic control, the rename on blur. So the form is a slot
 * (`renderFields`) and the noun is a parameter (`wording`), and what is left
 * belongs to every versioned object.
 *
 * ## One component for the existing entity AND for the new one
 *
 * `detail === null` is the new entity, and it is a STATE rather than a second
 * screen: §6 says « pas de boîte » and « avant ce premier enregistrement il
 * n'existe ni entité ni brouillon serveur : la saisie ne vit que dans la
 * page ». So the empty editor is this editor with nothing behind it - no
 * autosave, no history, no usage - and its first save writes the entity and the
 * v1 in the same transaction.
 *
 * ## The draft state is initialised from props and never by an effect
 *
 * The route resolves `detail` before mounting this component and keys it on the
 * entity's id, so `useState(() => kit.opening(detail))` runs exactly once per
 * entity. That is what keeps the repo's rule intact - no `useEffect` to copy a
 * query into state - and it is also what makes the reload of a stale draft a
 * REMOUNT rather than a state patch.
 *
 * ## The autosave is debounced in the handler, not in an effect
 *
 * A ref holds one timer; every keystroke pushes it back. No effect is involved,
 * which matters twice: the ban on `useEffect` for data is about exactly this
 * kind of write, and a timer restarted by a dependency array would fire on
 * renders that changed nothing.
 *
 * `baseUpdatedAt` travels with every autosave - the stamp of the draft this
 * editor has SEEN - and the server refuses when its own is newer. On that 409
 * the editor stops and offers to reload rather than overwriting somebody's
 * paragraph: last write wins, but only over a draft the writer had read (§4).
 *
 * The timer is dropped on unmount, and in two other places by hand: a
 * debounced write that outlives its screen is a write nobody can attribute to
 * anything, and both gestures that move the server draft themselves - a save
 * and a rename - would otherwise be followed by a PUT carrying a
 * `baseUpdatedAt` they have already superseded. That produces a « quelqu'un a
 * modifié ce brouillon » caused by the operator's own click, which is the one
 * refusal this screen must never invent.
 *
 * ## The rename is its own write
 *
 * §4: renaming updates the entity and the draft without creating a version. So
 * the name field commits on BLUR through `PATCH` while the rest of the content
 * rides the draft autosave - and a `name-taken` refusal lands on the field
 * rather than in a corner of the page. It reads `draft.name`, which is why
 * `Draft` is constrained to carry one: every versioned object has a name (§4),
 * and it is the only field of a draft this layer ever looks at.
 *
 * ## Five openings the protocol lot added, and what each one is for (issue #49)
 *
 * The measure of #42 was that a persona provided exactly the six things a
 * criterion provides. A protocol provides the same six and needs five more,
 * each one a sentence of §7.1 or of H17 rather than a convenience. All five are
 * OPTIONAL and default to what this file did before them, so the two objects
 * that came first emit the same nodes in the same order - the property
 * `criteres.spec.ts` and the capture of that screen rest on.
 *
 *   - `layout` - `'columns'` (the default) is the two-column editor of §4;
 *     `'drawer'` gives the form the full width and moves the history, the usage
 *     and the retirement into `HistoryDrawer`. §7.1 asks for it by name and
 *     gives the reason: a protocol's right column is taken by the step's rules,
 *     and it is « la seule divergence avec les éditeurs de critère et de
 *     persona ».
 *   - `renderHeaderExtra` - the node the head hangs before its buttons: the
 *     yellow « N problèmes » pill, which lists content this layer cannot read.
 *   - `renderSaveBlockers` - what the save box refuses, given the state of its
 *     production checkbox. §7.1 makes the version refusable on the content and
 *     the tick refusable on the criteria it cites, and both have to be said
 *     before the click rather than as a 409 after it.
 *   - `ownRefusal` - the object's own wording of a 409 the six common codes do
 *     not cover. A protocol answers `invalid-steps` and `production-unresolved`
 *     (`protocolErrorSchema`), which are states of the CONTENT and deliberately
 *     not in `versionedErrorCodeSchema`; unworded, both would have rendered the
 *     generic « n'a pas pu être enregistrée », which names nothing.
 *   - `canSave` - what arms the one button, REPLACING `canSaveVersion`. H17
 *     (§16) adds a fourth refusal to the three of §4, and it holds for a NEW
 *     protocol only: the turn cap of every step. It cannot ride
 *     `kit.normalized` - a capless step is a legal CONTENT and a refused
 *     REASON, and answering null there would have greyed the button of an
 *     existing protocol without saying why (see `protocol-draft.ts`). The
 *     default is the layer's own three refusals, unchanged.
 *
 * ## A THIRD logement for the same wide layout: the tabs (issue #53)
 *
 * §9.1 makes the experience's editor the second consumer of the wide layout the
 * protocol asked for, and it lodges the three panels differently: « la
 * disposition est partagée avec l'éditeur de protocole, qui a le même besoin et
 * loge ces trois panneaux dans un tiroir plutôt que dans un onglet : c'est la
 * même disposition, deux logements. » So `layout: 'tabs'` gives the body the
 * full width, draws the consumer's own tabs, appends « Historique » LAST and
 * renders the three panels inside it, in two columns of equal width (maquette
 * `OngletHistorique.dc.html`).
 *
 * The drawer stays the protocol's logement and the tabs stay the experience's:
 * neither migrates to the other. What the two share is everything above the
 * body - the head, the autosave, the boxes - which is what made one layout out
 * of two screens rather than two editors.
 *
 * Four more optional props, each falling back to what this file did before it:
 *
 *   - `tabs` - the object's own tabs, `{ id, label, render }`. `render` takes
 *     the SAME binding as `renderFields`, and not the draft alone: a tab that
 *     could read the draft but not write it could not carry the timeline, which
 *     is the whole of what the first tab is for. Each tab is a `renderFields`
 *     of its own, which is why there is one slot shape here and not two.
 *   - `tab` / `onSelectTab` - the selected tab travels in the URL (`?tab=`),
 *     the same régime as `?version=` and for the same reason: a reload, a back
 *     button or a pasted link land on the panel somebody was reading. The layer
 *     RESOLVES what it is given - a `?tab=` naming none of the tabs falls back
 *     to the first one rather than rendering nothing - and the consumer owns
 *     the navigation, because a `<Link>` written here could not typecheck
 *     against a route tree this component knows nothing about.
 *   - `headerFields` - the « champs » variant of the head (§9.1): the name
 *     drawn as a title, plus one field of the object's own. It is a function of
 *     the binding because the second field's value is IN the draft, and because
 *     the name's write is the only line that has to know the draft's shape -
 *     `{ ...draft, name }` does not type back to a generic `Draft`.
 *   - `breadcrumb` - « Expériences › Accueil PMS », above the head, which is
 *     what carries the identity of the screen once the `h1` has become a field.
 *     A node and not a route, for the reason `renderCardLink` exists - or a
 *     FUNCTION of the binding since #58, because the page editor's last segment
 *     names the page somebody is renaming, and a segment read off the server
 *     draft would have lagged the field beside it by one autosave.
 *
 * ## And one opening for a box that has to READ before it draws (issue #55)
 *
 *   - `onSaveBox` - fired with the CONTENT the box is about to save when it
 *     opens, and with `null` when it closes. §9.1 gives the experience's box an
 *     AVERTISSEMENT, which is not a refusal and can therefore never travel as a
 *     409, plus two refusals only the base can answer - so that box reads
 *     `POST /api/experiences/check` before it draws anything. Three things make
 *     it this shape rather than a boolean:
 *
 *       * the read has to be GATED on the box being open - un-gated it would
 *         fire on every keystroke, beside the autosave - and
 *         `renderSaveBlockers` cannot gate it, because a slot called during a
 *         child's render cannot start a query in its parent;
 *       * the dry run is about the content the save will POST, which is
 *         `kit.normalized(draft)` and which only this file holds;
 *       * « Ouvrir » on a refused line has to CLOSE the box before it
 *         navigates, and the box is this file's, so the door travels with the
 *         content.
 *
 *     Optional, and the three objects before it declare nothing.
 *
 *   - `onPointerBox` - its twin for « Passer en production » and « Reculer
 *     développement » (issue #154): the version and the pointer, when the box
 *     opens, and `null` when it closes. The experience asks the SERVER about
 *     the version it moves - `analyst-reading-missing` needs the readings of
 *     the protocol versions, which no card carries - so it too must start a
 *     read from the gesture and not from a slot.
 *
 * ## A FOURTH logement, and the one that carries no panel at all (issue #58)
 *
 * §9.1 opens the page editor « sans les trois onglets : il occupe l'écran seul,
 * sous un fil d'Ariane et un retour Timeline ». It is still the EXPERIENCE's
 * editor - the same entity, the same draft, the same autosave, the same one
 * button and the same save box - so it is this layer and not a screen of its
 * own; what it does not have is a place for the three panels of §4, which live
 * one click away in the tab the breadcrumb leads back to.
 *
 * So `layout: 'solo'` gives the body the full width and draws NO strip, NO
 * aside and NO drawer: the history, « utilisé dans » and the retirement are
 * simply not rendered. They are not lost - « Historique » is a tab of the
 * editor this screen returns to - and rendering them twice would be two places
 * to retire one experience from.
 *
 *   - `heading` - the `h1` and the line under it, both nodes built by the
 *     consumer (`editor-header.tsx`). The page editor's title is the PAGE's
 *     name and its subtitle names the experience, so neither the `h1` variant
 *     nor the `fields` variant can say it. A function of the binding and not a
 *     constant, because the page's name is in the draft.
 *
 * ## A FIFTH logement: a singleton in a tab of Réglages (issue #148)
 *
 * « Les textes des noyaux » is ONE versioned object with no library around it
 * (noyau.md §6.1, N8): no list, no card, no creation box, no retirement, no
 * rename. Its editor IS a tab of Réglages, under the shell's `h1` and the tab's
 * `h2`. So `layout: 'settings'` is the drawer logement minus what a singleton
 * cannot do: the form full width, the history and « utilisé dans » in the
 * drawer, NO retirement panel - hence no retirement box either - and the head
 * in its « panneau » variant (`editor-header.tsx`). The birth is the layer's
 * own: `detail === null` is the empty editor and its first save calls
 * `api.create`, which the object points at its first « versions » write.
 *
 * The review of !462 added two OPTIONAL props for it, each absent on the four
 * libraries, which therefore read, write and render nothing new:
 *
 *   - `browserDraft` - the entry of a NEW entity kept in the browser until its
 *     first version (`VersionedBrowserDraft`). Eighteen texts delivered a few
 *     at a time are exactly the partial entry N7 promises to keep, and before
 *     the v1 there is no entity to hold a server draft. Read by the
 *     initializer of the draft, written at the rhythm of the autosave, cleared
 *     by the first save that lands, and said by the head.
 *   - `referentsByTag` - who plays each pointer of the entity, drawn as given by
 *     the save box and « Passer en production », instead of `detail.usage`
 *     filtered on the referents' own pointers: an experience reads the texts
 *     under the tag of its REFERENCE (§6.5), and the first save needs the answer
 *     while there is no detail at all.
 *
 * ## A header shortcut for the gesture the history already had, except here (issue #108)
 *
 * §4 forbade this once: « Passer en production se fait depuis l'historique ou
 * par la case de la boîte d'enregistrement, jamais depuis l'en-tête. » Nicolas
 * reversed that call (2026-09-21) - the header now offers the same gesture
 * directly on the current `développement`, next to the button that saves it,
 * everywhere EXCEPT here: `solo` keeps its one button, for the same reason it
 * keeps no history, no « utilisé dans » and no retirement above - a page
 * editor is a SECOND mount of the experience, and a second place to push it
 * live is exactly what this logement exists to avoid. The eligibility rule
 * itself lives where it is computed, next to `onPassToProduction` below.
 *
 * `renderFields` is OPTIONAL since #53, and required in practice for the two
 * older layouts and for this one: under `tabs` the object's surface is its
 * tabs, so a consumer that passed `() => null` would be writing a slot to say
 * it has none.
 *
 * ## A draft is disposable, and never identical to a version (issue #185)
 *
 * Two halves of §4, both held HERE so the five editors inherit them. The head
 * offers « Abandonner le brouillon » as soon as the SERVER holds a draft - even
 * with « Enregistrer » grey - and its box says who changed the draft and when
 * (`discard-dialog.tsx`); confirmed, the answer is absorbed and the editor
 * REMOUNTS from the `développement` version, which is also what empties the
 * undo pile. The other half is on the wire: an autosave may now answer with NO
 * draft, the server deleting one identical to `développement` or `production`.
 * That is a success - what is typed stays on screen and the next autosave
 * starts from a null stamp - and `autosave`'s `onSuccess` below is where it is
 * absorbed.
 *
 * ## The undo pile, held HERE so the four editors inherit it (issue #135)
 *
 * There was no undo anywhere in this front end. It is built on the draft state
 * below rather than inside any one screen, for the reason the whole file
 * exists: the criterion, the persona, the protocol and the experience all type
 * into `setDraft`, so a pile wrapped around it is one lot for four editors and
 * for every editor a later lot adds.
 *
 * What it holds are SNAPSHOTS of the draft, capped at fifty
 * (`lib/undo-stack.ts`). Every edition in this repository is written by spread,
 * so consecutive snapshots share every branch the change did not touch: the
 * real cost is far below fifty times a draft, which is what makes that cap
 * generous rather than reckless.
 *
 * Two decisions are worth naming, because both were taken to avoid touching
 * anything else:
 *
 *   - **No call site of `onDraft` moves.** There are dozens of them across four
 *     editors, and asking each one to declare « this write is a keystroke » was
 *     the alternative. Instead the pile COALESCES on a clock: a write landing
 *     less than half a second after the previous one replaces the top of the
 *     pile instead of stacking, so a typed sentence is one entry. The rule is
 *     imperfect - a fast hand can merge two gestures - and it is the only one
 *     that costs no edit anywhere else.
 *   - **The shortcut is inert inside a field.** `Ctrl+Z` in an `input`, a
 *     `textarea`, a `select` or a `contentEditable` stays the browser's, word by
 *     word, which is what somebody typing actually wants; outside a field it is
 *     the pile's. The guard is `isEditableTarget` (`lib/editable-target.ts`),
 *     exported rather than inlined because #136 hangs `Suppr` on the same window
 *     and must not answer that question a second time.
 *
 * A REMOUNT starts from an empty pile, and that falls out of the state being
 * seeded by `useState`: « Recharger le brouillon du serveur » is a remount (the
 * route keys this editor on the entity), so there is no pile to clear and no
 * clearing to forget.
 */

const AUTOSAVE_DELAY_MS = 1200;

/**
 * The tab the layer appends, and the only one it renders itself (§9.1).
 *
 * Last in the strip, because the two before it are what somebody opens the
 * editor to do and this one is what they open it to check.
 */
const HISTORY_TAB = { id: 'historique', label: 'Historique' } as const;

/** Which box is open, if any. `null` is the resting state of the screen. */
type OpenBox =
  | { readonly kind: 'save' }
  | { readonly kind: 'pointer'; readonly tag: VersionTag; readonly target: VersionSummary }
  | { readonly kind: 'retire' }
  | { readonly kind: 'discard' };

/** What the object's form is handed, and everything it is allowed to do. */
export interface VersionedFieldsBinding<Draft> {
  /** What to render: the draft being typed, or the version « Voir » is showing. */
  readonly draft: Draft;
  /** A version opened by « Voir », or a retired entity: nothing is typable. */
  readonly readOnly: boolean;
  /** The rename refusal, worded from the server's `code`. Null when none. */
  readonly nameError: string | null;
  /** One draft change: the state, then a pushed-back autosave. */
  readonly onDraft: (next: Draft) => void;
  /** The name is committed on BLUR, because a rename is its own write (§4). */
  readonly onNameCommit: () => void;
}

/**
 * One tab of the object's own, in the wide layout's tabs logement (§9.1).
 *
 * `render` takes the binding `renderFields` takes, whose first member is the
 * draft: a tab is a `renderFields` scoped to a section, and the timeline writes
 * the draft like any other form of this layer.
 */
export interface VersionedTab<Draft> {
  /** What `?tab=` carries: « timeline ». Never « historique », which is the layer's. */
  readonly id: string;
  /** What the strip shows: « Timeline ». */
  readonly label: string;
  readonly render: (binding: VersionedFieldsBinding<Draft>) => ReactNode;
}

/**
 * What the object is handed when its save box opens (§9.1, `onSaveBox`).
 *
 * Opening the box drops the debounced autosave on purpose - the save it
 * prepares writes the content itself, and a PUT landing after it would carry a
 * superseded stamp. So a box that closes WITHOUT saving owes that write, and
 * both doors pay it (review of !474): `close`, for a navigation that keeps the
 * editor mounted, gives the held-back autosave back at once - « Annuler » does
 * the same -; `leave` exists for ONE gesture (issue #154), « Ouvrir le
 * protocole » under a warning of the experience's box, which leads to ANOTHER
 * screen: it writes the draft on screen NOW, the unmount dropping every timer
 * and the next keystroke that would have carried it never coming.
 *
 * Neither writes anything while the version is being SAVED (second review of
 * !474): the version carries the content, and the box's doors are inert
 * meanwhile anyway - the debt is then cleared by the save, or given back when
 * it fails.
 */
export interface SaveBoxHandle<Content> {
  readonly content: Content;
  readonly close: () => void;
  readonly leave: () => void;
}

/**
 * What the object is handed when a POINTER box opens - « Passer en
 * production », « Reculer développement » (issue #154, `onPointerBox`).
 *
 * The version and the pointer, and not the content: the layer reads the
 * content itself for `renderPointerBlockers`, and an object that must ask the
 * SERVER about that version - the experience, whose `analyst-reading-missing`
 * only the base can judge (noyau.md §3.7) - has to start its request from the
 * gesture, a slot called during a render being unable to. `leave` closes the
 * box for a navigation off this screen and writes the autosave still owed
 * first, the rule of `SaveBoxHandle.leave`: the one on its timer - this box
 * does not drop it, but the unmount would - and the one a cancelled save box
 * may have held back before it.
 */
export interface PointerBoxHandle {
  readonly target: VersionSummary;
  readonly tag: VersionTag;
  readonly close: () => void;
  readonly leave: () => void;
}

/**
 * What the object declares for the « champs » head, the rest being the layer's.
 *
 * `Pick` and not a second interface: `nameError`, `onNameCommit` and `readOnly`
 * are states this editor already holds, and an object asked to pass them would
 * be asked to hold the rename régime of §4 as well.
 */
export type VersionedHeaderFields = Pick<EditorHeaderFields, 'name' | 'second'>;

export interface VersionedEditorProps<
  /*
    `Content extends DraftContent`, and the constraint is a sentence of §4
    rather than a convenience: a version is COMPLETE and a draft is half-typed,
    so every complete content is a legal draft content. It is what lets « Voir »
    and « Repartir de cette version » hand a version's content straight to
    `kit.fromContent` without a cast the compiler could not check.
  */
  Content extends DraftContent,
  DraftContent,
  Draft extends { readonly name: string },
> {
  wording: VersionedWording;
  /** The ten calls of this object, from `createVersionedApi`. */
  api: VersionedApi<
    unknown,
    VersionedDetail<Content, DraftContent>,
    VersionedVersion<Content>,
    SaveVersionRequest<Content>,
    SaveDraftRequest<DraftContent>
  >;
  /** The object's draft shape and the two functions that cross to the wire. */
  kit: VersionedDraftKit<Content, DraftContent, Draft>;
  /**
   * The two TanStack Query key prefixes: `['criterion', email, id]` for this
   * editor's entry, `['criteria', email]` for the list a write invalidates.
   * Prefixes and not whole keys, because the email is this component's to read.
   */
  keys: { readonly detail: string; readonly list: string };
  /**
   * True for an object other objects CITE - the criterion, the protocol, the
   * persona, the texts of the noyaux - whose save box lists the referents that
   * pin an older version and offers to give them a new one (vitrine.md §3.3,
   * H22). The experience, which only links cite, leaves it false: its route
   * does not exist, and the box keeps the list it always had.
   */
  hasReferents?: boolean;
  /** The entity, or null for one that does not exist yet. */
  detail: VersionedDetail<Content, DraftContent> | null;
  /** A version opened read-only by « Voir », or null. */
  viewing: VersionedVersion<Content> | null;
  onCloseView: () => void;
  onView: (version: VersionSummary) => void;
  /** Re-read the entity from the server - what the stale-draft box offers. */
  onReload: () => void;
  /** Where the freshly created entity's editor lives - the caller navigates. */
  onCreated: (id: string) => void;
  /**
   * Select the name field as soon as the editor opens (§4.2).
   *
   * True exactly on the editor a duplication from a library CARD has just
   * navigated to - the only place the gesture lives since #123: the copy is
   * named « X (copie) », and that name is made to be typed over in the two
   * seconds that follow. It is also the only place a name TRIMMED to fit its
   * bound is visible, which is why §4.2 asks for the selection and not just the
   * focus.
   */
  selectName?: boolean;
  /**
   * The object's own form (§13 will mount the persona's outside this editor).
   *
   * Required in practice for `columns` and `drawer`; under `tabs` the object's
   * surface is its TABS and this slot is never called.
   */
  renderFields?: (binding: VersionedFieldsBinding<Draft>) => ReactNode;
  /**
   * Two columns (§4), the form full width with the three panels in a drawer
   * (§7.1), the same width with the three panels in a tab (§9.1), or the same
   * width with NO panel at all - the page editor, which reaches them through
   * the breadcrumb (§9.1, issue #58) - or, since issue #148, a SINGLETON in a
   * tab of Réglages: the form full width, the history and « utilisé dans » in
   * the drawer, NO retirement, and the head in its « panneau » variant.
   */
  layout?: 'columns' | 'drawer' | 'tabs' | 'solo' | 'settings';
  /** The object's own tabs, before the « Historique » the layer appends (§9.1). */
  tabs?: readonly VersionedTab<Draft>[];
  /** The tab the URL carries. One naming no tab falls back to the first (§9.1). */
  tab?: string;
  /** Where a tab click goes: the consumer writes `?tab=` into its own route. */
  onSelectTab?: (id: string) => void;
  /** The « champs » head of §9.1: the name as a title, plus one field of the object's. */
  headerFields?: (binding: VersionedFieldsBinding<Draft>) => VersionedHeaderFields;
  /** The « titre et sous-titre » head of the solo logement (§9.1, issue #58). */
  heading?: (binding: VersionedFieldsBinding<Draft>) => EditorHeading;
  /**
   * « Expériences › Accueil PMS », above the head - the caller builds the link.
   *
   * A function when a segment of it lives in the DRAFT (§9.1, issue #58); a
   * plain node when it does not, which is what the three screens before it
   * pass.
   */
  breadcrumb?: ReactNode | ((binding: VersionedFieldsBinding<Draft>) => ReactNode);
  /** One node in the head's actions row, before the buttons: the protocol's pill. */
  renderHeaderExtra?: (draft: Draft) => ReactNode;
  /** What the save box refuses, given the state of its production checkbox. */
  renderSaveBlockers?: (
    draft: Draft,
    alsoProduction: boolean,
  ) => { content: ReactNode; blocked: boolean } | null;
  /** The object's own wording of a 409 the six common codes do not cover. */
  ownRefusal?: (error: unknown) => string | null;
  /** What arms the one button. Absent: the three refusals of §4 (`canSaveVersion`). */
  canSave?: (draft: Draft, detail: VersionedDetail<Content, DraftContent> | null) => boolean;
  /**
   * The save box opened on this content, or closed - what a dry run listens to
   * (§9.1). `close` shuts the box and gives back the autosave its opening held
   * back, like « Annuler »; `leave` shuts it for a navigation OFF this screen
   * and writes that autosave first (issue #154, review of !474).
   */
  onSaveBox?: (opened: SaveBoxHandle<Content> | null) => void;
  /**
   * A pointer box opened on this version and this pointer, or closed - what a
   * dry run of the version it moves listens to (issue #154, `PointerBoxHandle`).
   */
  onPointerBox?: (opened: PointerBoxHandle | null) => void;
  /**
   * What the object refuses - or warns about - on the version a pointer box is
   * aimed at (§7.1), given that version's own CONTENT and the TAG that moves.
   *
   * The content and not the draft, and that is the whole reason this opening
   * exists rather than reusing `renderSaveBlockers`: the history moves the
   * pointer onto an OLD version, whose references are not the ones being typed.
   * So the layer reads that version when the box opens - once, scoped to the
   * target - and hands its content over. An object that declares nothing here
   * pays no read at all.
   *
   * The tag since issue #154: « Reculer développement » moves `development`
   * onto a version as able as any other to lack a reading for the analyst, and
   * the protocol names what that would stop (noyau.md §4.9, N37). A refusal
   * stays a matter of `production` - the experience and the protocol answer
   * null for `development`, and nothing that move did before it changes.
   */
  renderPointerBlockers?: (
    content: Content,
    tag: VersionTag,
  ) => { content: ReactNode; blocked: boolean } | null;
  /**
   * Where a NEW entity's entry is kept until its first version, for an object
   * that asks for it (review of !462, issue #148) - see
   * `VersionedBrowserDraft`.
   *
   * Used only while `detail === null`: read ONCE by the initializer of the
   * draft, written at the rhythm of the autosave (and when the save box opens,
   * which is the entry most worth keeping), cleared by the first save that
   * lands. The head says where it stands. Absent - the four libraries - the
   * editor reads nothing, writes nothing and says what it always said.
   */
  browserDraft?: VersionedBrowserDraft<Draft>;
  /**
   * Who plays what each box moves, per pointer OF THE ENTITY, when the object
   * knows it better than `detail.usage` (review of !462, issue #148).
   *
   * The layer's boxes filter « utilisé dans » by the referent's own pointer,
   * which ignores the tag of the citation. The texts of the noyaux cannot live
   * with that - an experience reads them under the tag of its REFERENCE
   * (noyau.md §6.5) - and they also need an answer before the entity exists,
   * where there is no detail at all. So the object hands the two lists here,
   * each line tagged with its own pointers that play the entity's pointer, and
   * the save box and « Passer en production » draw them as given. Absent, both
   * boxes read `detail.usage` as they always did.
   */
  referentsByTag?: Readonly<Record<VersionTag, readonly UsageLine[]>>;
}

export function VersionedEditor<
  Content extends DraftContent,
  DraftContent,
  Draft extends { readonly name: string },
>({
  wording,
  api,
  kit,
  keys,
  hasReferents = false,
  detail,
  viewing,
  onCloseView,
  onView,
  onReload,
  onCreated,
  selectName = false,
  renderFields,
  layout = 'columns',
  tabs = [],
  tab,
  onSelectTab,
  headerFields,
  heading,
  breadcrumb,
  renderHeaderExtra,
  renderPointerBlockers,
  renderSaveBlockers,
  ownRefusal,
  onSaveBox,
  onPointerBox,
  canSave = (next, entity) => canSaveVersion(next, entity, kit),
  browserDraft,
  referentsByTag,
}: VersionedEditorProps<Content, DraftContent, Draft>) {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  /**
   * The vitrine edits no model (docs/specs/vitrine.md section 7, issue #192):
   * this editor is the same one, rendered as a reading - the read-only régime of
   * `?version=`, with no gesture in the head and no write behind the fields.
   */
  const vitrine = useIsVitrine();

  /** This editor's own root, so the name field is looked up inside it and not in the document. */
  const root = useRef<HTMLElement>(null);

  /*
    What this browser kept of a NEW entity's entry (review of !462) - read once,
    here, by an initializer like the draft it seeds, and never while an entity
    exists: from its first version on, the server draft is the draft.
  */
  const [kept] = useState(() => (detail === null ? (browserDraft?.read() ?? null) : null));
  const [draft, setDraft] = useState<Draft>(() => kept?.draft ?? kit.opening(detail));
  /**
   * The draft on screen, for the doors a box hands out (`SaveBoxHandle`,
   * `PointerBoxHandle`): they are built when the box OPENS and called later,
   * so a closure would write the draft of that render. Moved by `write`, the
   * one place every draft change goes through.
   */
  const draftRef = useRef(draft);
  const [browserStatus, setBrowserStatus] = useState<BrowserDraftStatus | null>(
    kept === null ? null : { kind: 'restored', at: kept.keptAt },
  );
  /*
    Seeded from the draft above and not from `kit.opening(detail)` again: the
    opening of an object is allowed to be expensive (the persona's builds itself
    from the involvement scale), and two calls would also be two chances for the
    pile's floor to differ from what is on screen by one field.
  */
  const [pile, setPile] = useState<UndoStack<Draft>>(() => openUndoStack(draft));
  const [autosavedAt, setAutosavedAt] = useState<string | null>(null);
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<string | null>(
    detail?.draft?.updatedAt ?? null,
  );
  const [open, setOpen] = useState<OpenBox | null>(null);
  /**
   * Is the drawer open? Component state and NOT a search param, the régime the
   * « Plus de filtres » panel already holds: it is a posture of the reader and
   * not a property of what is on screen.
   */
  const [drawer, setDrawer] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * An autosave was pending when a save box opened, and the opening dropped it
   * (see `SaveBoxHandle`). Raised by every opening that drops one - never
   * lowered by an opening that finds none, or a box cancelled then reopened
   * would forget the write the first one held back (review of !474) - and
   * lowered only when that write is really made: an autosave that fires, or a
   * version that saves the content itself. A ref and not state: it is read by
   * a gesture and never drawn.
   */
  const heldBack = useRef(false);
  /**
   * A version is being saved: its POST is in flight (second review of !474).
   * A ref and not `save.isPending`, because it is read by the doors of a box,
   * which are built when the box OPENS - a closure would read that render's
   * answer, from before the click that started the save.
   */
  const savingVersion = useRef(false);

  /**
   * Every open and every close of a box goes through here, so the objects that
   * have to READ when a box opens hear about it (§9.1; the pointer box since
   * #154).
   *
   * A wrapper and not a call beside each `setOpen`: there are eight of them -
   * three openings, three cancels and two successes - and the one that got
   * forgotten would be a box left drawing the previous answer.
   */
  function showBox(next: OpenBox | null, saving: Content | null = null) {
    setOpen(next);
    onSaveBox?.(
      next?.kind === 'save' && saving !== null
        ? { content: saving, close: closeBox, leave: leaveBox }
        : null,
    );
    onPointerBox?.(
      next?.kind === 'pointer'
        ? { target: next.target, tag: next.tag, close: closeBox, leave: leaveBox }
        : null,
    );
  }

  /**
   * Pay what a closing box owes (review of !474): the autosave a save box held
   * back, and the one still on its timer - settled first, so it cannot write
   * the same draft a second time behind this one. Made NOW and not scheduled:
   * nothing else would ever write the first, and an unmount drops the second.
   * The draft is read from the ref: the doors were built when the box opened,
   * and a closure would write that render's.
   *
   * NOTHING while a version is being saved (second review of !474): the
   * version carries the content, and a PUT beside its POST would carry the
   * stamp from before it - accepted after the POST on an entity without a
   * draft, it resurrects a draft identical to the version; refused with a
   * `draft-stale` on one with a draft. The debt then stays: `onSuccess` of the
   * save clears it, `onError` gives it back.
   */
  function payOwedAutosave() {
    // A new entity has nothing to autosave onto, and its entry was kept in
    // the browser when its save box opened.
    if (detail === null || savingVersion.current) return;
    const owed = heldBack.current || timer.current !== null;
    settleAutosave();
    if (owed) autosave.mutate(draftRef.current);
  }

  /** A box closed without its gesture, the editor staying: « Annuler », or the `close` of a handle. */
  function closeBox() {
    showBox(null);
    payOwedAutosave();
  }

  /** A box closed for a navigation OFF this screen (issue #154): the same debt, the same payment. */
  function leaveBox() {
    showBox(null);
    payOwedAutosave();
  }

  /** Every write answers with the whole entity, so the caches are written and not guessed. */
  function absorb(next: VersionedDetail<Content, DraftContent>) {
    queryClient.setQueryData([keys.detail, email, next.id], next);
    void queryClient.invalidateQueries({ queryKey: [keys.list, email] });
    setBaseUpdatedAt(next.draft?.updatedAt ?? null);
  }

  const autosave = useMutation({
    mutationFn: (next: Draft) => {
      if (detail === null) throw new Error('no entity to autosave onto');
      return api.saveDraft(detail.id, { content: kit.payload(next), baseUpdatedAt });
    },
    // The write a save box held back is being made: nothing is owed any more.
    onMutate: () => {
      heldBack.current = false;
    },
    onSuccess: (next) => {
      /*
        An answer WITHOUT a draft is not an error (issue #185): the server keeps
        no draft identical to the `développement` or the `production` version,
        so a save that brought the content back to either one deleted it. What
        is typed stays on screen - nothing here touches the draft state - and
        `absorb` sends the next autosave from a null stamp, there being no draft
        to have read. The line under the title must then not claim a save that
        did not happen, so it falls back to « Brouillon en cours », or to
        « identique à la vN » when the content equals `développement`.
      */
      setAutosavedAt(next.draft?.updatedAt ?? null);
      absorb(next);
    },
  });

  const rename = useMutation({
    mutationFn: (name: string) => {
      if (detail === null) throw new Error('no entity to rename');
      return api.rename(detail.id, name);
    },
    onSuccess: absorb,
  });

  const save = useMutation({
    mutationFn: (input: { note: string | null; updateReferents: ReferentRef[] }) => {
      const content = kit.normalized(draft);
      if (content === null) throw new Error('the draft is not a version yet');
      const body: SaveVersionRequest<Content> = {
        content,
        // The passage to production has its own door (V15, vitrine.md): saving never moves it.
        alsoProduction: false,
        // Only when the box ticked some: the request of an object that nothing
        // cites, or of a save that updates nobody, is the one it always was.
        ...(input.updateReferents.length === 0 ? {} : { updateReferents: input.updateReferents }),
        // OMITTED and not `undefined`: `exactOptionalPropertyTypes` makes the
        // two different types, and the schema's `.optional()` means absent.
        ...(input.note === null ? {} : { note: input.note }),
      };
      return detail === null ? api.create(body) : api.saveVersion(detail.id, body);
    },
    onMutate: () => {
      savingVersion.current = true;
    },
    onSettled: () => {
      savingVersion.current = false;
    },
    // The version did not land: the autosave its box held back is owed again,
    // and given back at once (second review of !474).
    onError: () => {
      if (heldBack.current && detail !== null) autosave.mutate(draftRef.current);
    },
    onSuccess: (next, input) => {
      // The version carries the content the held-back autosave would have.
      heldBack.current = false;
      showBox(null);
      absorb(next);
      // The referents that got a version of their own (§3.3): their lists, their
      // editors and their usage lines are read again, under the prefixes their
      // own screens write.
      if (input.updateReferents.length > 0) {
        for (const prefix of ['protocols', 'protocol', 'experiences', 'experience']) {
          void queryClient.invalidateQueries({ queryKey: [prefix] });
        }
      }
      if (detail === null) {
        // The entry is a version now: what the browser kept of it is forgotten,
        // and a write still pending must not bring it back.
        settleAutosave();
        browserDraft?.clear();
        // The new entity had no address of its own; it has one now, and the
        // history, the usage and the retirement panel appear with it.
        onCreated(next.id);
      }
    },
  });

  const pointer = useMutation({
    mutationFn: (input: { tag: VersionTag; versionId: string }) => {
      if (detail === null) throw new Error('no entity to point at');
      return api.movePointer(detail.id, input);
    },
    onSuccess: (next) => {
      showBox(null);
      absorb(next);
    },
  });

  const retirement = useMutation({
    mutationFn: (input: { retire: boolean }) => {
      if (detail === null) throw new Error('no entity to retire');
      return input.retire ? api.retire(detail.id) : api.restore(detail.id);
    },
    onSuccess: (next) => {
      showBox(null);
      absorb(next);
    },
  });

  /**
   * « Abandonner le brouillon » (issue #185): the server draft is deleted, and
   * the editor starts again from the `développement` version.
   *
   * The start again is a REMOUNT, by the door the stale-draft box already
   * uses: `onReload` re-reads the entity and every route keys its editor on a
   * counter that the answer bumps. A remount is what empties the undo pile
   * (it is seeded by `useState`, so there is nothing to clear and no clearing
   * to forget) and re-seeds whatever the object's own form keeps beside the
   * draft. `absorb` writes the answer into the cache first, so the refetch
   * the route makes is confirming and not discovering.
   *
   * A pending autosave is dropped when the gesture fires, for the reason the
   * save and the rename drop theirs: a PUT landing behind the DELETE would
   * carry a stamp the DELETE has superseded, and resurrect the draft being
   * abandoned. The box is `busy` while one is in flight, so none is.
   *
   * A `draft-stale` refusal closes the box and raises the same callout as the
   * autosave's, with the same reload button (`staleRefusal` below): a draft
   * somebody else changed is exactly the one this editor must not delete
   * unseen. Any other failure stays in the box.
   */
  const discard = useMutation({
    mutationFn: () => {
      if (detail === null) throw new Error('no entity to discard a draft of');
      return api.discardDraft(detail.id, baseUpdatedAt);
    },
    onMutate: () => {
      settleAutosave();
      heldBack.current = false;
    },
    onSuccess: (next) => {
      showBox(null);
      absorb(next);
      // A version being read is no more the page the operator asked to start
      // again on: leave it, like « Repartir de cette version ».
      if (viewing !== null) onCloseView();
      onReload();
    },
    onError: (error) => {
      if (isDraftStale(error)) showBox(null);
    },
  });

  /**
   * « Repartir de cette version » - it reloads a version INTO the draft (§4).
   *
   * A mutation and not a `useQuery`, because it is a gesture: it fires on a
   * click, once, and its answer is written into state rather than rendered.
   * The confirmation §4 asks for is not needed here for the reason §4 gives -
   * it is required « seulement si le brouillon diffère de la version
   * développement », and this editor greys nothing: the draft is autosaved, so
   * what it replaces is recoverable from the history it was taken from.
   *
   * It CLOSES a read-only view on success, which is the feedback the gesture
   * would otherwise have none of: fired while « Voir » is showing a version,
   * it would replace a draft nothing on screen is displaying.
   */
  const restart = useMutation({
    mutationFn: (version: VersionSummary) => {
      if (detail === null) throw new Error('no entity to restart from');
      return api.readVersion(detail.id, version.id);
    },
    onSuccess: (loaded) => {
      if (viewing !== null) onCloseView();
      change(kit.fromContent(loaded.content));
    },
  });

  /**
   * One draft write: the state, then a pushed-back autosave - and NO pile entry.
   *
   * Split out of `change` for undo and redo alone, which put a draft on screen
   * that the pile already holds: routed through `change` they would record what
   * they had just replayed, and every undo would be its own undo.
   */
  function write(next: Draft) {
    // Belt and braces: the fields are read-only on the vitrine, and a draft
    // there would be a write the server refuses (403 `vitrine-read-only`).
    if (vitrine) return;
    setDraft(next);
    draftRef.current = next;
    if (detail === null) {
      // Nothing to autosave onto yet (§6) - but an object that keeps a new
      // entry in the browser keeps it at the autosave's own rhythm.
      if (browserDraft !== undefined) schedule(() => keepInBrowser(next));
      return;
    }
    schedule(() => autosave.mutate(next));
  }

  /** The debounce of every draft write: the last one within the delay wins. */
  function schedule(run: () => void) {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      run();
    }, AUTOSAVE_DELAY_MS);
  }

  /** Keep a new entity's entry in the browser, and let the head say how it went. */
  function keepInBrowser(next: Draft) {
    if (browserDraft === undefined) return;
    const at = browserDraft.write(next);
    setBrowserStatus(at === null ? { kind: 'refused' } : { kind: 'kept', at });
  }

  /** One draft change from a form: the write above, plus its entry in the pile. */
  function change(next: Draft) {
    setPile((stack) => recordUndo(stack, next, Date.now()));
    write(next);
  }

  /**
   * Walk the pile one step, and put what is there back on screen (issue #135).
   *
   * It writes like any other change - the autosave follows, debounced - because
   * an undo that only moved the screen would be undone again by the next
   * keystroke landing on a server draft that never heard about it.
   */
  function stepHistory(direction: 'undo' | 'redo') {
    const moved = direction === 'undo' ? undoEntry(pile) : redoEntry(pile);
    if (moved === null) return;
    setPile(moved.stack);
    write(moved.value);
  }

  /** Drop a pending autosave: the gesture about to fire writes the draft itself. */
  function settleAutosave() {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }

  /*
    And the same drop on unmount. An EFFECT in a repo that proscribes them, and
    the ban is intact: it forbids `useEffect` + fetch as a way of LOADING data,
    and this one fetches nothing - it releases a timer.

    It reads the REF and not `settleAutosave`, which matters: that function is
    rebuilt every render, so naming it would put it in the dependency list, and
    a dependency that changes every render fires the cleanup every render -
    cancelling the autosave on each keystroke, which is the exact opposite of
    what a debounce is for. A ref has a stable identity, so the empty list is
    honest and the cleanup runs once, when the screen goes.
  */
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  /**
   * The content of the version a pointer box is aimed at, read only while that
   * box is open and only for an object that has something of its own to say
   * about it (§7.1; and, since #154, the reading of a protocol, whichever the
   * pointer - noyau.md §4.9).
   *
   * `enabled` carries all three conditions, because a hook cannot be
   * conditional and because none of them alone is enough: no entity, no dialog,
   * or an object with nothing to say, and this reads nothing at all. The key
   * names the target version, so opening the box on two different lines does
   * not serve the first one's answer for the second - and not the tag, the
   * content of a version being the same whichever pointer moves onto it.
   */
  /*
    The plan of the referents, read each time the save box opens and never kept:
    a colleague may have saved a version of a referent a minute ago, and a
    stale plan would offer a line the server then finds already moved. `enabled`
    only for an object that has referents and an entity to ask about.
  */
  const referentPlan = useQuery({
    queryKey: [keys.detail, email, detail?.id ?? null, 'referents'],
    queryFn: () => api.referents(detail?.id ?? ''),
    enabled: hasReferents && detail !== null && open?.kind === 'save',
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const pointerOpen = open?.kind === 'pointer' ? open : null;
  // A retirement goes through the prod first when the entity has a version there (§4.1, H13): the
  // question « is a prod linked » is asked only when the box opens, and only for such an entity.
  const inProd =
    detail !== null &&
    (detail.production !== null || detail.versions.some((version) => version.sentToProdAt != null));
  const prodLinkedForRetire = useProdLinked(open?.kind === 'retire' && inProd);
  const retireViaProd = inProd && prodLinkedForRetire.data === true;
  const pointerVersion = useQuery({
    queryKey: [keys.detail, email, detail?.id ?? null, 'pointeur', pointerOpen?.target.id ?? null],
    queryFn: () => api.readVersion(detail?.id ?? '', pointerOpen?.target.id ?? ''),
    enabled: detail !== null && pointerOpen !== null && renderPointerBlockers !== undefined,
    retry: false,
  });
  /*
    Null while the read is in flight, and that is deliberate: a box that blocked
    on « we do not know yet » would refuse a legitimate move for as long as the
    read takes. The server refuses either way (§7.1), so what is at stake here
    is a round trip and not a guarantee.
  */
  const pointerBlockers =
    pointerVersion.data === undefined || pointerOpen === null
      ? null
      : (renderPointerBlockers?.(pointerVersion.data.content, pointerOpen.tag) ?? null);

  const content = kit.normalized(draft);
  const identical =
    detail !== null && content !== null && structurallyEqual(content, detail.developmentContent);
  /*
    `detail !== null &&` spelled out rather than `detail?.retiredAt !== null`,
    which is the trap this line was written wrong once already: on a NEW entity
    `detail?.retiredAt` is `undefined`, `undefined !== null` is true, and the
    empty editor would have opened read-only.
  */
  const retired = detail !== null && detail.retiredAt !== null;
  const readOnly = viewing !== null || retired || vitrine;
  const shown = viewing === null ? draft : kit.fromContent(viewing.content);
  const busy =
    pointer.isPending ||
    retirement.isPending ||
    restart.isPending ||
    save.isPending ||
    discard.isPending;

  /**
   * The two chords, on the window (issue #135).
   *
   * `ctrlKey || metaKey`, so `Cmd+Z` works on a Mac without a platform read -
   * the two modifiers never both mean something else here. `Ctrl+Maj+Z` AND
   * `Ctrl+Y` both redo: the two conventions coexist in the wild, whoever learnt
   * one is right, and honouring both is a single extra comparison.
   *
   * Inert inside a field, which is `isEditableTarget`'s whole job - see the
   * header of this file, and `lib/editable-target.ts` for the rule itself. Inert
   * in read-only too, for the reason the buttons are absent there: a version
   * opened by « Voir » and a retired entity have no draft to walk back through.
   * And inert while a box is open (second review of !474): the box shows the
   * effect of ONE content, and an undo behind it would move the draft under
   * the save it prepares and start a timer that writes it twice.
   *
   * NO dependency array, deliberately. The handler reads the pile and the
   * read-only state off the render it was built in, and a listener subscribed
   * once would keep the FIRST of them for the life of the screen - undoing onto
   * a snapshot taken before anything was typed. Re-subscribing a window listener
   * per render is two calls the browser resolves in constant time; the
   * alternative, a ref refreshed by an effect of its own, runs exactly as often
   * with one indirection more.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const key = event.key.toLowerCase();
      const undoing = key === 'z' && !event.shiftKey;
      const redoing = (key === 'z' && event.shiftKey) || key === 'y';
      if (!undoing && !redoing) return;
      if (readOnly || open !== null || isEditableTarget(event.target)) return;
      // Only once the chord is OURS: a `preventDefault` before the guards above
      // would have swallowed the browser's own undo inside every field.
      event.preventDefault();
      stepHistory(undoing ? 'undo' : 'redo');
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const renameRefusal = versionedRefusalOf(rename.error);
  /*
    The refusal that has a reload behind it: the autosave's, or the abandon's
    `draft-stale` alone - its other failures say themselves inside the box.
  */
  const staleRefusal =
    versionedRefusalOf(autosave.error) ??
    (isDraftStale(discard.error) ? versionedRefusalOf(discard.error) : null);

  /**
   * The refusal of a failed write, worded from its `code`, or a generic
   * sentence.
   *
   * Three answers and not two since #49, tried in order: the object's OWN
   * refusal, then the six common codes, then the generic sentence. The object
   * goes first because its codes are the narrower catalogue - a protocol's
   * `invalid-steps` is not one of the six, so `versionedRefusalOf` answers null
   * on it and the generic sentence, which names nothing, would have won.
   *
   * The three must never appear together: a typed refusal already says what it
   * is, and the generic one is for a failure that carried no code at all.
   */
  function refusalOf(error: unknown, fallback: string): string | null {
    if (error === null || error === undefined) return null;
    const own = ownRefusal?.(error) ?? null;
    if (own !== null) return own;
    const refusal = versionedRefusalOf(error);
    return refusal === null ? fallback : versionedRefusal(refusal, wording.refusal);
  }

  /** The singleton of a Réglages tab (issue #148): the drawer, without the retirement. */
  const settings = layout === 'settings';
  const drawered = layout === 'drawer' || settings;
  const tabbed = layout === 'tabs';
  /** The page editor: full width, no strip, no aside, no drawer (§9.1). */
  const solo = layout === 'solo';

  /**
   * The header's own « Passer en production » shortcut (issue #108, reversing
   * §4's old « jamais depuis l'en-tête », 2026-09-21): a callback when
   * `développement` is AHEAD of `production` - the catalogue's own
   * `developmentAhead` (`versioning/cards.ts`, backend): no `production` yet,
   * or a higher number, never a plain inequality, so a `développement` rolled
   * back behind `production` (§4's « Reculer développement ») does not arm it.
   *
   * Closed on the same three grounds the history's hover icon already closes
   * on - a write in flight, a retired entity, a read-only view of some OTHER
   * version - plus `solo`: a page editor is a second mount of the experience,
   * and that layout already renders no history, no « utilisé dans » and no
   * retirement for exactly the same reason (see the FOURTH logement above).
   *
   * `undefined` and not a disabled callback: its PRESENCE is the toggle
   * `editor-header.tsx` reads, so an ineligible state gets no button at all -
   * one derived value rather than a boolean plus a second null check at the
   * call site, since TypeScript cannot narrow `developmentVersion` back OUT of
   * a separately named boolean.
   */
  const developmentVersion = detail?.development ?? null;
  const productionVersion = detail?.production ?? null;
  const onPassToProduction =
    !busy &&
    !vitrine &&
    !retired &&
    !solo &&
    viewing === null &&
    developmentVersion !== null &&
    (productionVersion === null || developmentVersion.number > productionVersion.number)
      ? () => showBox({ kind: 'pointer', tag: 'production', target: developmentVersion })
      : undefined;

  /**
   * The header's « Abandonner le brouillon » (issue #185): a callback as soon
   * as the SERVER holds a draft, `undefined` - no button - otherwise.
   *
   * On `detail.draft` and not on the local draft or on `canSave`, and that is
   * the point of the spec: the button is there when « Enregistrer » is grey
   * (a draft that equals nothing to save still sits on the server, and it is
   * what the cards call « brouillon en attente »), and it is not there for an
   * operator who has typed nothing the server has seen. A retired entity
   * keeps it - the server admits the abandon there, and a draft left on it
   * must not stay - and so does a version being read: the draft is still the
   * entity's. Closed while a write is in flight, like its sibling above.
   */
  const onDiscardDraft =
    !busy && !vitrine && detail !== null && detail.draft !== null && developmentVersion !== null
      ? () => showBox({ kind: 'discard' })
      : undefined;

  /**
   * The tab being shown, or null for « Historique » - the layer's own.
   *
   * Three answers in one expression, and the order is the rule of §9.1: the tab
   * the URL names, then « Historique » when that is what it names, then the
   * FIRST tab for anything else. A `?tab=` somebody mistyped therefore opens a
   * panel rather than an empty page, and an object with no tabs of its own gets
   * the history, which is the only tab it has.
   */
  const shownTab =
    tabs.find((entry) => entry.id === tab) ?? (tab === HISTORY_TAB.id ? null : (tabs[0] ?? null));

  /** What the object's form and each of its tabs are handed (§4, §9.1). */
  const binding: VersionedFieldsBinding<Draft> = {
    draft: shown,
    readOnly,
    nameError: renameRefusal === null ? null : versionedRefusal(renameRefusal, wording.refusal),
    onDraft: change,
    onNameCommit: () => {
      // The rename moves the SERVER DRAFT as well as the entity (§4), so
      // a PUT landing after it would carry a stamp it has superseded.
      settleAutosave();
      const name = draft.name.trim();
      // Nothing to rename: no entity yet, an unchanged name, or an empty
      // one - the server refuses an empty name, and the mark on the field
      // has already said so.
      if (detail === null || name === '' || name === detail.name) return;
      rename.mutate(name);
    },
  };

  const declaredFields = headerFields?.(binding);
  const declaredHeading = heading?.(binding);

  /** The three panels of §4, each named because the tabs lay them out in two columns. */
  const history = (
    <VersionHistory
      entity={wording.entity}
      versions={detail?.versions ?? []}
      production={detail?.production ?? null}
      development={detail?.development ?? null}
      retired={retired}
      busy={busy}
      onView={onView}
      onMovePointer={(tag, target) => showBox({ kind: 'pointer', tag, target })}
      onRestartFrom={(version) => restart.mutate(version)}
      onRestore={() => retirement.mutate({ retire: false })}
    />
  );

  /*
    ABSENT and not empty when nothing cites the entity: the panel returns null
    on an empty list, « un usage nul ne s'affiche pas » (§4). The column keeps
    the history and the retirement, and so does the tab.
  */
  const usagePanel = <UsagePanel entity={wording.entity} usage={detail?.usage ?? []} />;

  /*
    A singleton has no retirement (N8), so neither the panel nor the box that
    opens from it exists in the `settings` logement.
  */
  const retirementPanel =
    detail === null || settings || vitrine ? null : (
      <RetirementPanel
        wording={wording}
        name={detail.name}
        retiredAt={detail.retiredAt}
        busy={busy}
        onRetire={() => showBox({ kind: 'retire' })}
        onRestore={() => retirement.mutate({ retire: false })}
      />
    );

  /**
   * The copy opens with its NAME SELECTED (§4.2).
   *
   * Found by the `data-testid` every object already puts on its name field -
   * `critere-nom`, `persona-nom`, `protocole-nom`, `experience-nom` - and
   * looked up inside THIS editor's own root rather than the document: the field
   * lives in the object's form under two layouts and in the header under the
   * other two, so a ref threaded down would be four props for one gesture,
   * where the mark is a contract all four already honour.
   *
   * `select` and not just `focus`: the name is « X (copie) », made to be typed
   * over, and a caret at its end would ask for a deletion first. It is also the
   * one place where a name CUT to fit its bound becomes visible (§4.2).
   *
   * Once, on mount: the editor is remounted per entity (its `key` is the id),
   * so « the copy just opened » and « this component just mounted » are the same
   * instant, and re-running it on a later render would steal the caret from
   * somebody who had moved on.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: on mount only - see above.
  useEffect(() => {
    if (!selectName) return;
    const field = root.current?.querySelector(`[data-testid="${wording.entity}-nom"]`);
    if (field instanceof HTMLInputElement) {
      field.focus();
      field.select();
    }
  }, []);

  /** The same three, stacked, which is what the column and the drawer take. */
  const panels = (
    <>
      {history}
      {usagePanel}
      {retirementPanel}
    </>
  );

  return (
    <section ref={root} className="space-y-6 pb-16">
      {typeof breadcrumb === 'function' ? breadcrumb(binding) : breadcrumb}
      <EditorHeader
        wording={wording}
        name={detail?.name ?? null}
        production={detail?.production ?? null}
        development={detail?.development ?? null}
        serverDraft={detail?.draft ?? null}
        identical={identical}
        autosavedAt={autosavedAt}
        nextNumber={nextVersionNumber(detail)}
        canSave={canSave(draft, detail) && viewing === null && !vitrine}
        saving={save.isPending}
        onSave={() => {
          // Whether the drop just below loses a write - what the doors of the
          // box put back. RAISED and never lowered here: an earlier box closed
          // without writing may already owe one (review of !474).
          if (detail !== null && timer.current !== null) heldBack.current = true;
          settleAutosave();
          // A new entity's entry is kept NOW rather than dropped with the
          // pending write: the box may be cancelled or its save refused, and
          // the entry it opened on is the one most worth keeping.
          if (detail === null) keepInBrowser(draft);
          // The content and not the draft: the dry run of §9.1 is about what
          // the save will POST, and that is what `kit.normalized` answers.
          showBox({ kind: 'save' }, content);
        }}
        // Spread and not `extra={...}`: `exactOptionalPropertyTypes` makes an
        // explicit `undefined` a type error, so an absent slot is an absent KEY.
        {...(renderHeaderExtra === undefined ? {} : { extra: renderHeaderExtra(shown) })}
        {...(drawered ? { onOpenHistory: () => setDrawer(true) } : {})}
        {...(onPassToProduction === undefined ? {} : { onPassToProduction })}
        {...(onDiscardDraft === undefined ? {} : { onDiscardDraft })}
        {
          /* ABSENT in read-only, and not two dead icons: there is no draft to
            walk back through on a version « Voir » is showing or on a retired
            entity, and an off control says « not now » where nothing at all
            says « not here ». */
          ...(readOnly
            ? {}
            : {
                undoRedo: {
                  canUndo: canUndo(pile),
                  canRedo: canRedo(pile),
                  onUndo: () => stepHistory('undo'),
                  onRedo: () => stepHistory('redo'),
                },
              })
        }
        {...(declaredFields === undefined
          ? {}
          : {
              fields: {
                ...declaredFields,
                nameError: binding.nameError,
                onNameCommit: binding.onNameCommit,
                readOnly,
              },
            })}
        {...(declaredHeading === undefined ? {} : { heading: declaredHeading })}
        {...(settings ? { panel: true } : {})}
        {...(browserDraft === undefined ? {} : { browserDraft: browserStatus })}
      />

      {/*
        The one refusal of the autosave that has a gesture behind it. It is a
        `danger` callout and not a toast: the write did NOT happen, so the box
        has to stay on screen until somebody decides between reloading and
        copying their paragraph out by hand.
      */}
      {staleRefusal !== null ? (
        <Callout tone="danger" live="alert" data-testid={`${wording.entity}-brouillon-perime`}>
          <span className="block">{versionedRefusal(staleRefusal, wording.refusal)}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            data-testid={`${wording.entity}-recharger-brouillon`}
            onClick={() => {
              settleAutosave();
              autosave.reset();
              discard.reset();
              onReload();
            }}
          >
            Recharger le brouillon du serveur
          </Button>
        </Callout>
      ) : null}

      {autosave.isError && staleRefusal === null ? (
        <Callout tone="warn" live="status">
          Le brouillon n’a pas pu être enregistré. Votre saisie est toujours à l’écran ; la
          prochaine modification réessaiera.
        </Callout>
      ) : null}

      {viewing === null ? null : (
        <Callout tone="info" data-testid={`${wording.entity}-lecture-seule`}>
          <span className="block">
            Vous lisez la v{viewing.version.number} en lecture seule. Rien n’est modifiable ici.
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            data-testid={`${wording.entity}-fermer-lecture`}
            onClick={onCloseView}
          >
            {vitrine ? VITRINE_WORDING.backToLatest : 'Revenir au brouillon'}
          </Button>
        </Callout>
      )}

      {tabbed ? (
        <>
          <VersionedTabs
            entity={wording.entity}
            tabs={[...tabs, HISTORY_TAB]}
            active={shownTab?.id ?? HISTORY_TAB.id}
            onSelect={(id) => onSelectTab?.(id)}
          />

          {shownTab === null ? (
            /*
              Two columns of EQUAL width (maquette `OngletHistorique`): the
              history on the left, « utilisé dans » and the retirement stacked
              on the right. Equal and not the `380px` of the two-column layout,
              because nothing shares this row: that column is narrow to leave a
              FORM the rest of the page, and here the three panels are the whole
              of it - a fixed 380 px would have stranded them beside a history
              three times their width.
            */
            <div
              data-testid={`${wording.entity}-onglet-panneau`}
              className="grid items-start gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
            >
              {history}
              <div className="space-y-6">
                {usagePanel}
                {retirementPanel}
              </div>
            </div>
          ) : (
            <div data-testid={`${wording.entity}-onglet-panneau`}>{shownTab.render(binding)}</div>
          )}
        </>
      ) : (
        <div
          className={
            drawered || solo
              ? 'space-y-6'
              : 'grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_380px]'
          }
        >
          {renderFields?.(binding)}

          {drawered || solo ? null : <aside className="space-y-6">{panels}</aside>}
        </div>
      )}

      {drawered && drawer ? (
        <HistoryDrawer
          entity={wording.entity}
          onClose={() => setDrawer(false)}
          {...(settings ? { label: 'Historique et utilisation' } : {})}
        >
          {panels}
        </HistoryDrawer>
      ) : null}

      {open?.kind === 'save' && content !== null ? (
        <SaveDialog
          wording={wording}
          name={draft.name.trim()}
          nextNumber={nextVersionNumber(detail)}
          usage={detail?.usage ?? []}
          {...(referentsByTag === undefined ? {} : { referentsByTag })}
          {...(hasReferents && detail !== null ? { referents: { plan: referentPlan.data } } : {})}
          /* Busy while a PUT of the draft is in flight too - the one a failed
             save gives back, or the one a timer fired just before the box
             opened: a POST started beside it would race it (second review of
             !474). Nothing else autosaves behind an open box. */
          saving={save.isPending || autosave.isPending}
          error={refusalOf(save.error, 'La version n’a pas pu être enregistrée.')}
          {...(renderSaveBlockers === undefined
            ? {}
            : {
                renderBlockers: (alsoProduction: boolean) =>
                  renderSaveBlockers(shown, alsoProduction),
              })}
          onConfirm={(input) => save.mutate(input)}
          onCancel={() => {
            save.reset();
            // And the autosave its opening held back, given back (review of !474).
            closeBox();
          }}
        />
      ) : null}

      {open?.kind === 'pointer' && detail !== null ? (
        <PassageBox
          entityId={detail.id}
          wording={wording}
          name={detail.name}
          tag={open.tag}
          target={open.target}
          current={open.tag === 'production' ? detail.production : detail.development}
          usage={detail.usage}
          {...(referentsByTag === undefined ? {} : { referentsByTag })}
          busy={pointer.isPending}
          blockers={pointerBlockers}
          error={refusalOf(pointer.error, 'Le pointeur n’a pas pu être déplacé.')}
          onConfirm={() => pointer.mutate({ tag: open.tag, versionId: open.target.id })}
          onPassed={() => {
            // The cascade moved other entities too (vitrine.md §4.2): this one, its list and
            // everything that cites or is cited by it are read again.
            for (const prefix of [keys.detail, keys.list, ...CASCADE_QUERY_PREFIXES]) {
              void queryClient.invalidateQueries({ queryKey: [prefix] });
            }
          }}
          onCancel={() => {
            pointer.reset();
            closeBox();
          }}
        />
      ) : null}

      {open?.kind === 'discard' && detail !== null && detail.draft !== null ? (
        <DiscardDialog
          wording={wording}
          draft={detail.draft}
          developmentNumber={developmentVersion?.number ?? 0}
          /* Busy while an autosave is in flight too: the DELETE carries the
             stamp this editor has read, and the PUT about to land would make
             it stale by the operator's own hand - the one refusal this screen
             must never invent. */
          busy={discard.isPending || autosave.isPending}
          error={refusalOf(discard.error, 'Le brouillon n’a pas pu être abandonné.')}
          onConfirm={() => discard.mutate()}
          onCancel={() => {
            discard.reset();
            showBox(null);
          }}
        />
      ) : null}

      {open?.kind === 'retire' && detail !== null ? (
        <RetireDialog
          wording={wording}
          name={detail.name}
          usage={detail.usage}
          viaProd={retireViaProd}
          busy={retirement.isPending}
          error={refusalOf(retirement.error, 'Le retrait a échoué.')}
          onConfirm={() => retirement.mutate({ retire: true })}
          onCancel={() => {
            retirement.reset();
            showBox(null);
          }}
        />
      ) : null}
    </section>
  );
}
